import { forwardRef } from "react";

// eslint-disable-next-line no-restricted-imports
import { Stack, SxProps, Theme, useTheme } from "@mui/material";
import { formatDateOnly } from "@simplyk/common";

import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { useTranslate } from "@/common/hooks/useTranslate";
import { FormIcon } from "@/forms-design-system/FormIcon";
import { FormTypography } from "@/forms-design-system/FormTypography/FormTypography";
import { FormWelcomeBanner } from "@/forms-design-system/FormWelcomeBanner/FormWelcomeBanner";
import { InvalidThemeErrorMessage } from "@/forms-design-system/InvalidThemeErrorMessage";
import { Card } from "@/icons/outlined";

export interface MembershipItem {
  title: string;
  expirationDate: Date | string | null;
  isAutoRenewal: boolean;
}

export interface FormMemberBannerProps {
  memberName: string;
  memberships: MembershipItem[];
  sx?: SxProps<Theme>;
}

export const FormMemberBanner = forwardRef<HTMLDivElement, FormMemberBannerProps>(
  ({ memberName, memberships, sx }, ref) => {
    const theme = useTheme();
    const { t } = useTranslate();
    const { locale } = useLocaleContext();
    const { isPhoneScreen } = useMediaQuery();

    if (!theme.palette.forms) {
      return <InvalidThemeErrorMessage />;
    }

    const formatDate = (date: Date | string) =>
      formatDateOnly(date, locale, { year: "numeric", month: "long", day: "numeric" });

    const getRenewalText = (membership: MembershipItem) => {
      if (!membership.expirationDate) {
        return t("common", "memberBanner.noExpiration");
      }
      if (membership.isAutoRenewal) {
        return t("common", "memberBanner.autoRenews", { date: formatDate(membership.expirationDate) });
      }
      return t("common", "memberBanner.expires", { date: formatDate(membership.expirationDate) });
    };

    const details = isPhoneScreen ? (
      <Stack gap={0.5}>
        {memberships.map((membership, index) => (
          <Stack key={index} gap={0.25}>
            <Stack direction="row" alignItems="center" gap={0.5}>
              <FormIcon size="xsmall" color={theme.palette.forms.text.brand}>
                <Card />
              </FormIcon>
              <FormTypography variant="caption" vibe="brand">
                {membership.title}
              </FormTypography>
            </Stack>
            <FormTypography variant="caption" vibe="brand">
              {getRenewalText(membership)}
            </FormTypography>
          </Stack>
        ))}
      </Stack>
    ) : (
      memberships.map((membership, index) => (
        <Stack key={index} direction="row" alignItems="center" gap={1.5}>
          <Stack direction="row" alignItems="center" gap={0.5}>
            <FormIcon size="xsmall" color={theme.palette.forms.text.brand}>
              <Card />
            </FormIcon>
            <FormTypography variant="body2" vibe="brand">
              {membership.title}
            </FormTypography>
          </Stack>
          <FormTypography variant="body2" vibe="brand">
            •
          </FormTypography>
          <FormTypography variant="body2" vibe="brand">
            {getRenewalText(membership)}
          </FormTypography>
        </Stack>
      ))
    );

    return <FormWelcomeBanner ref={ref} memberName={memberName} details={details} sx={sx} />;
  }
);

FormMemberBanner.displayName = "FormMemberBanner";
